import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NumbersConfigEnum, PhoneNumberModel, PhoneNumberDataModel, ColumnModel, DataViewEnum } from '../models';
import { RootState } from '../app/store';
import { setFormattedData, addOrUpdateData } from '../helpers/helper';
import { voiceOptions, supportedLocales } from '../constants';

export interface NumbersState {
    currentNumbersConfig: NumbersConfigEnum;
    dataView: DataViewEnum;
    data: PhoneNumberDataModel;
}

const initialState: NumbersState = {
    currentNumbersConfig: NumbersConfigEnum.Active,
    dataView: DataViewEnum.List,
    data: {
        recordCount: 0,
        records: [],
        rawData: [],
        columns: []
    },
};

const uniqueDataKey = "businessPhoneNumberId";

const columnConfig: Record<string, Partial<ColumnModel>> = {
    primaryKeyId: {
        width: 60,
        formatter: (record: PhoneNumberModel) => record.businessPhoneNumberId
    },
    phoneNumber: {
        width: 160
    },
    introduction: {
        width: 350,
        minWidth: 350,
        hasTooltip: true,
        hasInput: true,
        flexGrow: 1
    },
    voice: {
        width: 170,
        selectOptions: voiceOptions
    },
    language: {
        width: 270,
        selectOptions: supportedLocales
    },
    monthlyCost: {
        width: 160,
        formatter: (record: PhoneNumberModel) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(record.monthlyCost),
    },
    purchasedOn: {
        width: 180,
        formatter: (record: PhoneNumberModel) => new Date(record.purchasedOn).toLocaleString()
    },
};

export const numbersSlice = createSlice({
    name: 'numbers',
    initialState,
    reducers: {
        setNumbersType: (state, action: PayloadAction<NumbersConfigEnum>) => {
            state.currentNumbersConfig = action.payload;
            return state;
        },
        setDataView: (state, action: PayloadAction<DataViewEnum>) => {
            state.dataView = action.payload;
            return state;
        },
        setNumbersData: (state, action: PayloadAction<PhoneNumberModel[]>) => {
            if (action.payload)
                state.data = setFormattedData({ rawData: action.payload }, columnConfig, uniqueDataKey);

            return state;
        },
        setNumber: (state, action: PayloadAction<PhoneNumberModel>) => {
            addOrUpdateData(state.data, action.payload, columnConfig, uniqueDataKey);
            return state;
        },
        removeNumber: (state, action: PayloadAction<number>) => {
            state.data.records = state.data.records.filter(record => record[uniqueDataKey] !== action.payload);
            state.data.rawData = state.data.rawData.filter(raw => raw[uniqueDataKey] !== action.payload);
            return state
        },
        setLogoutNumbers: (state) => {
            state = initialState;
            return state;
        },
    },
});

export const { setNumbersType, setDataView, setNumbersData, setNumber, removeNumber, setLogoutNumbers } = numbersSlice.actions;

export default numbersSlice.reducer;